import { useEffect, useState, useMemo, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotStartedOutlinedIcon from '@mui/icons-material/NotStartedOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';

import CommonButton from '../../../../components/buttons/CommonButton';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import {
  exportSettlementPaymentDetails,
  getPaymentDetails,
  releaseSettlement,
  retrySettlementPayment,
  retryVendorPayment,
  retryVendorPaymentWithUpdate,
  retryAttyFeeVendorPayment,
  retryAttyFeeVendorPaymentWithUpdate,
  getDocumentSettlement,
  retryClientExternalAccountPayment,
  retryNetsuite,
  stopCheckTransaction,
  stopDeductionTransaction,
  stopAttyFeeTransaction,
} from '../../../../api/paymentsApi/settlementsApi';
import { accountingVendorType } from '../../../VendorsView/helpers';
import { useParams, useOutletContext, useLocation } from 'react-router-dom';
import Loader from '../../../../components/Loader';
import useSnackBar from '../../../../hooks/useSnackBar';
import {
  AttorneysFeeStatus,
  DeductionStatus,
  DispositionStatus,
  EntityFlexibleRecord,
  ErrorData,
  NetSettlementStatus,
  PaymentMethodStatus,
  SettlementPaymentDetails,
  SettlementStatus,
  DeductionField,
  ApprovalDetails as ApprovalDetailsType,
  AccountingSyncStatus,
} from '../../../../types';
import VerificationPopup from '../../../../components/VerificationPopup';
import usePageTitle from '../../../../hooks/usePageTitle';
import { StatusChip } from '../../../../components/StatusChip';
import { saveSettlementCsv } from '../../../../helpers/saveCsv';
import {
  AttorneysFeeStatusMap,
  NetSettlementStatusMap,
  SettlementPaymentFilterStatusMap,
  DetailsFilter,
  SettlementStatusMap,
} from '../constants';
import theme from '../../../../theme';
import DetailsDeductionTable from './DetailsDeductionTable';
import DetailsAttorneyTable from './DetailsAttorneyTable';
import DetailsNetSettlementTable from './DetailsNetSettlementTable';
import { getDocument } from '../../../../api/paymentsApi/deductionsApi';
import { renderAttorneyFees, renderTotals } from '../../SettlementsDetailsSummaryMapper';
import DetailsHoverCard from '../../../../components/DetailsHoverCard';
import { reInitiateCustomerOnboarding } from '../../../../api/clientsApi/clientsApi';
import ApprovalDetails from './ApprovalDetails';

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '32px',
    paddingTop: '10px',
  },
  coloredHeadlineText: {
    color: 'rgba(37, 157, 168, 0.72)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '120%',
    fontFamily: 'Poppins',
  },
  settledAmountText: {
    color: theme.palette.common.white,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
    fontFamily: 'Poppins',
  },
  accountText: {
    color: theme.palette.common.white,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    fontFamily: 'Poppins',
  },
  commonText: {
    color: theme.palette.common.white,
    fontWeight: 500,
    lineHeight: 'normal',
    fontStyle: 'normal',
  },
  fontSize12: {
    fontSize: '12px',
  },
  fontSize14: {
    fontSize: '14px',
  },
  fontSize16: {
    fontSize: '16px',
  },
  fontSize20: {
    fontSize: '20px',
  },
  fontSize24: {
    fontSize: '24px',
  },
  fontSize32: {
    fontSize: '32px',
  },
  fontFamilyPoppins: {
    fontFamily: 'Poppins',
  },
  fontFamilyInter: {
    fontFamily: 'Poppins',
  },
  fontWeight500: {
    fontWeight: 500,
  },
  fontWeight700: {
    fontWeight: 700,
  },
  textGrayColor: {
    color: theme.palette.common.white,
    opacity: 0.5,
  },
  settlementDetailsContainer: {
    display: 'flex',
    width: '100%',
    gap: '24px',

    '@media (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  settlementFundingContainer: {
    display: 'flex',
    width: '80%',
    gap: '24px',
    flexDirection: 'column',
    paddingTop: '5px',
  },
  paymentDetailsContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  settlementDetailsSection: {
    display: 'flex',
    flexBasis: '200px',
    padding: '32px',
    borderRadius: '16px',
    gap: '32px',
    boxSizing: 'border-box',
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,

    '@media (max-width: 1300px)': {
      flexDirection: 'column',
      gap: '64px',
    },

    '@media (max-width: 900px)': {
      padding: '12px',
      flexBasis: 0,
    },
  },
  settlementSummarySection: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '150px',
    borderRadius: '16px',
    gap: '5px',
    boxSizing: 'border-box',
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,

    '@media (max-width: 1300px)': {
      flexDirection: 'column',
      gap: '5px',
    },

    '@media (max-width: 900px)': {
      padding: '12px',
      flexBasis: 0,
    },
  },
  accountInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    flexBasis: '420px',
    width: '100%',

    '@media (max-width: 1300px)': {
      flexBasis: 0,
    },
  },
  distributionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '420px',
    width: '100%',
    gap: '32px',

    '@media (max-width: 1300px)': {
      flexBasis: 0,
    },
  },
  distributionContainer: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    padding: '10px',
  },
  distributionStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  distributionsSectionDivider: {
    height: '2px',
    background: theme.palette.secondary.dark,
  },
  summarySectionDivider: {
    width: '100%',
    height: '2px',
    background: '#282B3A',
  },
  fundingSectionDivider: {
    height: '1px',
    background: theme.palette.secondary.dark,
    marginTop: '8px',
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '168px',

    '@media (max-width: 1200px)': {
      maxWidth: '250px',
    },
  },
  formSectionsDivider: {
    width: '1px',
    background: theme.palette.secondary.dark,
    height: '100%',

    '@media (max-width: 1300px)': {
      display: 'none',
    },
  },
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '400px',
    width: '100%',
    justifyContent: 'center',
  },
  noDetailsTextBlock: {
    display: 'flex',
    height: '452px',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#35394B',
    borderRadius: '16px',
    padding: '32px',
  },
  noDetailsText: {
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '120%',
    textAlign: 'center',
    color: theme.palette.common.white,
  },
  responsiveDistributionContainer: {
    '@media (max-width: 700px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '10px',
    },

    '@media (max-width: 550px)': {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  minResponsiveContainer: {
    '@media (max-width: 376px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  errorMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    gap: '12px',
    background: theme.palette.primary.light,
    width: '100%',
    margin: '12px 0',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    wordBreak: 'break-word',
  },
  errorMessageText: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.error.main,
  },
  warningMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '16px',
    gap: '12px',
    background: theme.palette.primary.light,
    width: '100%',
    margin: '3px',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.warning.main}`,
    wordBreak: 'break-word',
  },
  infoMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '16px',
    gap: '12px',
    background: theme.palette.primary.light,
    width: '100%',
    margin: '3px',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: '1px solid #38BDF8',
    wordBreak: 'break-word',
  },
  warningMessageText: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.warning.main,
    margin: '3px',
  },
  infoMessageText: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: '#38BDF8',
    margin: '3px',
  },
  tabsContainer: {
    '& .MuiTabs-root': {
      minHeight: 'auto',
    },
    '& .MuiTab-root': {
      color: theme.palette.text.disabled,
      fontSize: '16px',
      fontWeight: 500,
      fontFamily: 'Poppins',
      textTransform: 'capitalize',
      minHeight: '48px',
      '&.Mui-selected': {
        color: theme.palette.common.white,
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.info.main,
    },
  },
  accordionRoot: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
    borderRadius: '16px',
    '&.MuiAccordion-root, &.MuiPaper-root': {
      borderRadius: '16px !important',
    },
    '&:first-of-type, &:last-of-type': {
      borderRadius: '16px !important',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: '#2e3243',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3), // reduced gap
    fontFamily: 'Poppins',
  },

  sectionBlock: {
    marginBottom: '24px',
  },

  sectionClient: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
  },

  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'center',
    padding: '6px 0',
    width: '100%',
  },

  label: {
    color: '#E0E0E0',
    fontSize: '14px',
    fontFamily: 'Poppins',
  },

  amountCenter: {
    color: '#FFFFFF',
    fontSize: '14px',
    textAlign: 'right',
    fontFamily: 'Poppins',
  },
  amountBold: {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '16px',
    width: '120px',
    textAlign: 'right',
  },

  statusBadge: {
    padding: '2px 12px',
    fontSize: '12px',
    justifySelf: 'end', // ★ Right aligned
  },
  // Inline styles moved to classes
  infoIconClickableBox: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    paddingBottom: '12px',
  },
  infoIcon: {
    fontSize: 16,
    color: 'white',
  },
}));

interface VerificationPopupState {
  isOpen: boolean;
  action: Action;
}

enum Action {
  Retry,
  Release,
  RetryVendorPayment,
  None,
}

type AttorneyFirmField = {
  id: number | string;
  name: string;
  amount: string; // formatted as currency string
  status: AttorneysFeeStatus;
  role?: string;
  counselEntityType?: 'FIRM' | 'VENDOR';
  paymentMethod?: PaymentMethodStatus;
  disposition?: DispositionStatus;
  attyFeeId?: number | null;
  vendorParticipationId?: number | null;
  paymentAccountId?: number | null;
  paymentAccountName?: string | null;
  memo?: string | null;
  canRetryVendorPayment?: boolean;
  canStopDeductionTransaction?: boolean;
  paymentErrorMessage?: string | null;
  accounting?: {
    billError: string;
    billLink: string;
    billSyncStatus?: AccountingSyncStatus | null;
    billPaymentError: string;
    billPaymentLink: string;
    billPaymentSyncStatus?: AccountingSyncStatus | null;
    vendorLink?: string;
  };
};

type NetSettlementField = {
  id: string;
  netSettlementId?: number | string;
  name: string;
  amount: number;
  status: NetSettlementStatus;
  disposition: DispositionStatus;
  paymentMethod: PaymentMethodStatus;
  canRetryPlaintiffPayment?: boolean;
  isEligibleForStopTransaction?: boolean;
  accounting?: {
    billError?: string;
    billLink?: string;
    billSyncStatus?: AccountingSyncStatus | null;
    billPaymentError?: string;
    billPaymentLink?: string;
    billPaymentSyncStatus?: AccountingSyncStatus | null;
    vendorLink?: string;
  };
  plaintiffPaymentError?: string;
};

type WaveData = {
  attorneysFees: AttorneyFirmField[];
  deductions: DeductionField[];
  netSettlement?: NetSettlementField;
  deductionsTotal: number;
  totalPaidCount: number;
  totalFailedCount: number;
  totalNotStartedCount: number;
  totalFundingCount: number;
  totalInDistributionCount: number;
  approvalDetails?: ApprovalDetailsType;
  // Lien totals
  totalLienFailed: number;
  totalLienNotStarted: number;
  totalLienFunding: number;
  totalLienInDistribution: number;
  totalLienPaid: number;
  // Service provider totals
  totalServiceProviderFailed: number;
  totalServiceProviderNotStarted: number;
  totalServiceProviderFunding: number;
  totalServiceProviderInDistribution: number;
  totalServiceProviderPaid: number;
  // Expense totals
  totalExpenseFailed: number;
  totalExpenseNotStarted: number;
  totalExpenseFunding: number;
  totalExpenseInDistribution: number;
  totalExpensePaid: number;
  // Lien counts
  lienFailedCount: number;
  lienNotStartedCount: number;
  lienFundingCount: number;
  lienInDistributionCount: number;
  lienPaidCount: number;
  // Service provider counts
  serviceProviderFailedCount: number;
  serviceProviderNotStartedCount: number;
  serviceProviderFundingCount: number;
  serviceProviderInDistributionCount: number;
  serviceProviderPaidCount: number;
  // Expense counts
  expenseFailedCount: number;
  expenseNotStartedCount: number;
  expenseFundingCount: number;
  expenseInDistributionCount: number;
  expensePaidCount: number;
};

type WavesDataMap = {
  [key: string]: WaveData; // <-- dynamic wave1, wave2, wave3...
};

// Type for individual distribution items from SettlementPaymentDetails
type SettlementDistributionItem = NonNullable<SettlementPaymentDetails['distributions']>[number];

const SettlementsDetailsView = () => {
  const { settlementId } = useParams();
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const { setPageTitle } = usePageTitle();
  const { entityData } = useOutletContext<EntityFlexibleRecord>();

  const vendorType = accountingVendorType;

  const [settlementDetails, setSettlementDetails] = useState<SettlementPaymentDetails>();
  const [isPaymentDetailsFetching, setIsPaymentDetailsFetching] = useState<boolean>(true);
  const [verificationPopupState, setVerificationPopupState] = useState<VerificationPopupState>({
    isOpen: false,
    action: Action.None,
  });
  const [noDetailsMessage, setNoDetailsMessage] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<
    'NotStarted' | 'Failed' | 'Paid' | 'Funding' | 'InDistribution'
  >();
  const [wavesData, setWavesData] = useState<WavesDataMap>({});
  const [activeWave, setActiveWave] = useState<string>('wave1');

  const showUserOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';
  const showApprovalFlow = localStorage.getItem('hasSettlementApprovalFlow') === 'true';

  const location = useLocation();
  const delayMs = location.state?.delayApiCall ? location.state?.delayMs || 500 : 0;

  const [hoverCardAnchorEl, setHoverCardAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredWave, setHoveredWave] = useState<SettlementDistributionItem | null>(null);
  const hoverCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCloseVerificationPopup = () =>
    setVerificationPopupState({ isOpen: false, action: Action.None });
  // const handleOpenVerificationPopup = (action: Action) =>
  //   setVerificationPopupState({ isOpen: true, action });

  const handleHoverCardOpen = (
    event: React.MouseEvent<HTMLDivElement>,
    wave: SettlementDistributionItem,
  ) => {
    // Clear any pending close timeout
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
    setHoverCardAnchorEl(event.currentTarget);
    setHoveredWave(wave);
  };

  const handleHoverCardClose = () => {
    // Add a small delay before closing to prevent flickering
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverCardAnchorEl(null);
      setHoveredWave(null);
    }, 100);
  };

  const handleHoverCardMouseEnter = () => {
    // Cancel any pending close when hovering over the popover
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  };

  const handleRetryPaymentProcess = (pin: string) => {
    if (!settlementId) {
      return;
    }

    handleCloseVerificationPopup();
    setIsPaymentDetailsFetching(true);

    retrySettlementPayment(Number(settlementId), pin)
      .then((res) => {
        if (res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'The settlement payment process has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleReleaseSettlement = (pin: string) => {
    if (!settlementId) {
      return;
    }

    handleCloseVerificationPopup();
    setIsPaymentDetailsFetching(true);

    releaseSettlement(Number(settlementId), pin)
      .then((res) => {
        if (res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'The settlement payment process has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleRetryVendorPayment = (deductionId: number | string) => {
    setIsPaymentDetailsFetching(true);

    retryVendorPayment(Number(deductionId))
      .then((res) => {
        if (res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'The vendor payment retry has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleRetryVendorPaymentWithUpdate = (
    deductionId: number | string,
    paymentMethod: string,
    vendorPaymentAccountId?: number | null,
    vendorPaymentAccountName?: string | null,
  ) => {
    const parsedDeductionId = Number(deductionId);
    if (!Number.isFinite(parsedDeductionId) || parsedDeductionId <= 0) {
      setAlert((prev) => ({
        ...prev,
        message: 'Invalid deduction id for retry action.',
        type: 'error',
      }));
      return;
    }

    setIsPaymentDetailsFetching(true);

    retryVendorPaymentWithUpdate({
      deductionId: parsedDeductionId,
      paymentMethod,
      vendorPaymentAccountId: vendorPaymentAccountId ?? null,
    })
      .then((res) => {
        if (res.status === 200 || res.status === 202) {
          setWavesData((prev) => {
            const next: WavesDataMap = { ...prev };
            Object.keys(next).forEach((waveKey) => {
              const currentWave = next[waveKey];
              next[waveKey] = {
                ...currentWave,
                deductions: currentWave.deductions.map((deduction) =>
                  Number(deduction.id) === parsedDeductionId
                    ? {
                      ...deduction,
                      paymentMethod: paymentMethod as PaymentMethodStatus,
                      paymentAccountId: vendorPaymentAccountId ?? null,
                      paymentAccountName: vendorPaymentAccountName ?? null,
                    }
                    : deduction,
                ),
              };
            });
            return next;
          });

          setAlert((prev) => ({
            ...prev,
            message: 'The vendor payment retry has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const {
    totalStatusCounts,
    filteredAttorneyFeesFields,
    filteredDeductionsFields,
    filteredNetSettlement,
    filteredApprovalDetails,
  } = useMemo(() => {
    const wave = wavesData[activeWave];

    if (!wave)
      return {
        totalStatusCounts: { Funding: 0, Paid: 0, Failed: 0, NotStarted: 0, InDistribution: 0 },
        filteredAttorneyFeesFields: [],
        filteredDeductionsFields: [],
        filteredNetSettlement: {} as NetSettlementField,
        filteredApprovalDetails: {} as ApprovalDetailsType,
      };

    const { attorneysFees, deductions, netSettlement, approvalDetails } = wave;

    const counts = {
      Funding: wave.totalFundingCount || 0,
      Paid: wave.totalPaidCount || 0,
      Failed: wave.totalFailedCount || 0,
      NotStarted: wave.totalNotStartedCount || 0,
      InDistribution: wave.totalInDistributionCount || 0,
    };

    const applyFilter = <T extends { status: string }>(items: T[]): T[] => {
      if (!activeFilter) return items;
      return items.filter((item) => {
        if (activeFilter === 'NotStarted') return item.status === 'NotStarted';
        if (activeFilter === 'Paid') return item.status === 'Paid';
        if (activeFilter === 'Funding') return item.status === 'Funding';
        if (activeFilter === 'InDistribution') return item.status === 'InDistribution';
        if (activeFilter === 'Failed')
          return ['Error', 'CriticalError', 'Failed'].includes(item.status);
        return true;
      });
    };

    return {
      totalStatusCounts: counts,
      filteredAttorneyFeesFields: applyFilter((attorneysFees as AttorneyFirmField[]) || []),
      filteredDeductionsFields: applyFilter((deductions as DeductionField[]) || []),
      filteredNetSettlement: applyFilter(
        netSettlement && netSettlement.amount !== 0 ? [netSettlement] : [],
      )[0],
      filteredApprovalDetails: approvalDetails,
    };
  }, [wavesData, activeWave, activeFilter]);

  const getActionHandler = (action: Action) => {
    switch (action) {
      case Action.Retry:
        return handleRetryPaymentProcess;
      case Action.Release:
        return handleReleaseSettlement;
      case Action.None:
        return;
    }
  };

  const getDetails = () => {
    if (!settlementId) {
      return;
    }
    getPaymentDetails(Number(settlementId))
      .then((res) => {
        if (res.data) {
          setNoDetailsMessage(undefined);
          setSettlementDetails(res.data);
          if (Array.isArray(res.data.distributions)) {
            const waves: Record<string, WaveData> = {};
            res.data.distributions.forEach((waveItem) => {
              const waveKey = `wave${waveItem.sequenceNumber}`;

              waves[waveKey] = {
                attorneysFees: [],
                deductions: [],
                netSettlement: undefined,
                deductionsTotal: waveItem.deductionsTotalAmount ?? 0,
                totalPaidCount: waveItem.totalPaidCount ?? 0,
                totalFailedCount: waveItem.totalFailedCount ?? 0,
                totalNotStartedCount: waveItem.totalNotStartedCount ?? 0,
                totalFundingCount: waveItem.totalFundingCount ?? 0,
                totalInDistributionCount: waveItem.totalInDistributionCount ?? 0,
                approvalDetails: waveItem.approvalDetails,
                totalLienFailed: waveItem.totalLienFailed ?? 0,
                totalLienNotStarted: waveItem.totalLienNotStarted ?? 0,
                totalLienFunding: waveItem.totalLienFunding ?? 0,
                totalLienInDistribution: waveItem.totalLienInDistribution ?? 0,
                totalLienPaid: waveItem.totalLienPaid ?? 0,
                totalServiceProviderFailed: waveItem.totalServiceProviderFailed ?? 0,
                totalServiceProviderNotStarted: waveItem.totalServiceProviderNotStarted ?? 0,
                totalServiceProviderFunding: waveItem.totalServiceProviderFunding ?? 0,
                totalServiceProviderInDistribution:
                  waveItem.totalServiceProviderInDistribution ?? 0,
                totalServiceProviderPaid: waveItem.totalServiceProviderPaid ?? 0,
                totalExpenseFailed: waveItem.totalExpenseFailed ?? 0,
                totalExpenseNotStarted: waveItem.totalExpenseNotStarted ?? 0,
                totalExpenseFunding: waveItem.totalExpenseFunding ?? 0,
                totalExpenseInDistribution: waveItem.totalExpenseInDistribution ?? 0,
                totalExpensePaid: waveItem.totalExpensePaid ?? 0,
                lienFailedCount: waveItem.lienFailedCount ?? 0,
                lienNotStartedCount: waveItem.lienNotStartedCount ?? 0,
                lienFundingCount: waveItem.lienFundingCount ?? 0,
                lienInDistributionCount: waveItem.lienInDistributionCount ?? 0,
                lienPaidCount: waveItem.lienPaidCount ?? 0,
                serviceProviderFailedCount: waveItem.serviceProviderFailedCount ?? 0,
                serviceProviderNotStartedCount: waveItem.serviceProviderNotStartedCount ?? 0,
                serviceProviderFundingCount: waveItem.serviceProviderFundingCount ?? 0,
                serviceProviderInDistributionCount:
                  waveItem.serviceProviderInDistributionCount ?? 0,
                serviceProviderPaidCount: waveItem.serviceProviderPaidCount ?? 0,
                expenseFailedCount: waveItem.expenseFailedCount ?? 0,
                expenseNotStartedCount: waveItem.expenseNotStartedCount ?? 0,
                expenseFundingCount: waveItem.expenseFundingCount ?? 0,
                expenseInDistributionCount: waveItem.expenseInDistributionCount ?? 0,
                expensePaidCount: waveItem.expensePaidCount ?? 0,
              };

              // Attorneys fees
              if (waveItem.attorneysFees) {
                const mappedLeadFirm: AttorneyFirmField[] = waveItem.attorneysFees.leadFirm
                  ? [
                    {
                      id: waveItem.attorneysFees.leadFirm.id ?? '',
                      name: waveItem.attorneysFees.leadFirm.name ?? '',
                      amount: numberToUSDCurrency(waveItem.attorneysFees.leadFirm.amount ?? 0),
                      status: (waveItem.attorneysFees.leadFirm.status ??
                        '') as AttorneysFeeStatus,
                      role: waveItem.attorneysFees.leadFirm.role,
                      paymentMethod: waveItem.attorneysFees.leadFirm
                        .paymentMethod as PaymentMethodStatus,
                      disposition: waveItem.attorneysFees.leadFirm
                        .disposition as DispositionStatus,
                      accounting: waveItem.attorneysFees.leadFirm.accounting
                        ? {
                          billError: waveItem.attorneysFees.leadFirm.accounting.billError ?? '',
                          billLink: waveItem.attorneysFees.leadFirm.accounting.billLink ?? '',
                          billSyncStatus:
                            waveItem.attorneysFees.leadFirm.accounting.billSyncStatus ?? null,
                          billPaymentError:
                            waveItem.attorneysFees.leadFirm.accounting.billPaymentError ?? '',
                          billPaymentLink:
                            waveItem.attorneysFees.leadFirm.accounting.billPaymentLink ?? '',
                          billPaymentSyncStatus:
                            waveItem.attorneysFees.leadFirm.accounting.billPaymentSyncStatus ??
                            null,
                          vendorLink:
                            waveItem.attorneysFees.leadFirm.accounting.vendorLink ?? '',
                        }
                        : undefined,
                    },
                  ]
                  : [];

                const mappedSecondaryFirms: AttorneyFirmField[] = Array.isArray(
                  waveItem.attorneysFees.secondaryFirms,
                )
                  ? waveItem.attorneysFees.secondaryFirms.map(
                      (
                        firm: SettlementDistributionItem['attorneysFees']['secondaryFirms'][number],
                      ) => ({
                        id: firm.id ?? '',
                        name: firm.name ?? '',
                        amount: numberToUSDCurrency(firm.amount ?? 0),
                        status: (firm.status ?? '') as AttorneysFeeStatus,
                        role: firm.role,
                        paymentMethod: firm.paymentMethod as PaymentMethodStatus,
                        disposition: firm.disposition as DispositionStatus,
                        counselEntityType: firm.counselEntityType ?? 'FIRM',
                        attyFeeId: firm.attyFeeId ?? null,
                        vendorParticipationId: firm.vendorParticipationId ?? null,
                        paymentAccountId: firm.paymentAccountId ?? null,
                        paymentAccountName: firm.paymentAccountName ?? null,
                        memo: firm.memo ?? null,
                        canRetryVendorPayment: firm.canRetryVendorPayment ?? false,
                        canStopDeductionTransaction: firm.canStopDeductionTransaction ?? false,
                        paymentErrorMessage: firm.paymentErrorMessage ?? null,
                        accounting: firm.accounting
                          ? {
                              billError: firm.accounting.billError ?? '',
                              billLink: firm.accounting.billLink ?? '',
                              billSyncStatus: firm.accounting.billSyncStatus ?? null,
                              billPaymentError: firm.accounting.billPaymentError ?? '',
                              billPaymentLink: firm.accounting.billPaymentLink ?? '',
                              billPaymentSyncStatus: firm.accounting.billPaymentSyncStatus ?? null,
                              vendorLink: firm.accounting.vendorLink ?? '',
                            }
                          : undefined,
                      }),
                    )
                  : [];

                const mappedFirms = [...mappedLeadFirm, ...mappedSecondaryFirms];

                waves[waveKey].attorneysFees = mappedFirms;
              }

              // Deductions
              if (waveItem.deductions) {
                const mappedDeductions: DeductionField[] = waveItem.deductions.map(
                  (d: SettlementDistributionItem['deductions'][number]) => ({
                    id: d.id,
                    vendorName: d.vendorName ?? '',
                    description: d.description !== null ? d.description : undefined,
                    amount: d.amount,
                    status: d.status as DeductionStatus,
                    type: d.type,
                    disposition: d.disposition,
                    paymentMethod: d.paymentMethod as PaymentMethodStatus,
                    paymentAccountId: d.paymentAccountId ?? d.vendorPaymentAccountId ?? null,
                    paymentAccountName: d.paymentAccountName ?? d.vendorPaymentAccountName ?? null,
                    canRetryVendorPayment: d.canRetryVendorPayment,
                    canStopDeductionTransaction: d.canStopDeductionTransaction ?? false,
                    paymentErrorMessage: d.paymentErrorMessage,
                    hasDocument: d.hasDocument,
                    accounting: d.accounting
                      ? {
                        billError: d.accounting.billError ?? '',
                        billLink: d.accounting.billLink ?? '',
                        billSyncStatus: d.accounting.billSyncStatus ?? null,
                        billPaymentError: d.accounting.billPaymentError ?? '',
                        billPaymentLink: d.accounting.billPaymentLink ?? '',
                        billPaymentSyncStatus: d.accounting.billPaymentSyncStatus ?? null,
                        vendorLink: d.accounting.vendorLink ?? null,
                      }
                      : undefined,
                  }),
                );
                waves[waveKey].deductions = mappedDeductions;
              }

              // Net Settlement
              if (waveItem.netSettlement) {
                const actualNetSettlementId =
                  waveItem.netSettlement?.netSettlementId ?? waveItem.netSettlement?.id ?? '';

                waves[waveKey].netSettlement = {
                  id: String(actualNetSettlementId),
                  netSettlementId: actualNetSettlementId,
                  name: waveItem.netSettlement?.name ?? '',
                  amount: waveItem.netSettlement?.amount ?? 0,
                  status: waveItem.netSettlement?.status ?? '',
                  disposition: waveItem.netSettlement?.disposition ?? '',
                  paymentMethod: waveItem.netSettlement?.paymentMethod ?? '',
                  canRetryPlaintiffPayment:
                    waveItem.netSettlement?.canRetryPlaintiffPayment ?? false,
                  isEligibleForStopTransaction:
                    waveItem.netSettlement?.isEligibleForStopTransaction ??
                    waveItem.netSettlement?.isAligibleForStopTransaction ??
                    false,
                  plaintiffPaymentError: waveItem.netSettlement?.plaintiffPaymentError ?? '',
                  accounting: {
                    billError: waveItem.netSettlement?.accounting?.billError ?? '',
                    billLink: waveItem.netSettlement?.accounting?.billLink ?? '',
                    billSyncStatus: waveItem.netSettlement?.accounting?.billSyncStatus ?? null,
                    billPaymentError: waveItem.netSettlement?.accounting?.billPaymentError ?? '',
                    billPaymentLink: waveItem.netSettlement?.accounting?.billPaymentLink ?? '',
                    billPaymentSyncStatus:
                      waveItem.netSettlement?.accounting?.billPaymentSyncStatus ?? null,
                    vendorLink: waveItem.netSettlement?.accounting?.vendorLink ?? '',
                  },
                };
              }
            });
            setWavesData(waves);

            // Default: first wave
            const firstKey = Object.keys(waves)[0];
            if (firstKey) setActiveWave(firstKey);
          }
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        const conflictMessage =
          error.response?.status === 409 ? error.response?.data?.message?.trim() : undefined;

        setNoDetailsMessage(conflictMessage || undefined);
        setSettlementDetails(undefined);
      })
      .finally(() => {
        setIsPaymentDetailsFetching(false);
      });
  };

  useEffect(() => {
    setPageTitle && setPageTitle('Settlement Details');

    const timer = setTimeout(() => {
      getDetails();
    }, delayMs);

    return () => {
      clearTimeout(timer);
      setPageTitle && setPageTitle('');
    };
  }, [settlementId, delayMs]);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  const exportList = async () => {
    exportSettlementPaymentDetails(Number(settlementId))
      .then((response) => {
        saveSettlementCsv(response.data, `settlement-${settlementId}-disbursement-details`);
        setAlert({
          message: 'Settlements exported successfully.',
          type: 'success',
        });
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleViewDocument = async () => {
    const documentKey = settlementDetails?.docpath;
    if (!documentKey) {
      setAlert((prev) => ({
        ...prev,
        message: 'Document not found.',
        type: 'info',
      }));
      return;
    }

    try {
      const encodedDocumentKey = encodeURIComponent(documentKey);
      const response = await getDocumentSettlement(encodedDocumentKey);
      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;
        // Trigger download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = ''; // Browser will use the filename from the URL
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: response.data?.message || 'No file found to view.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'File view error',
        type: 'error',
      }));
    }
  };

  if (isPaymentDetailsFetching) {
    return (
      <Box className={classes.loaderContainer}>
        <Loader colorType="warning" />
      </Box>
    );
  }

  if (!settlementDetails) {
    return (
      <Box className={classes.noDetailsTextBlock}>
        <Typography className={classes.noDetailsText}>
          {noDetailsMessage ? (
            noDetailsMessage
          ) : (
            <>
              No data is currently available.
              <br />
              Transaction details will be displayed once a settlement is initiated for payment.
            </>
          )}
        </Typography>
      </Box>
    );
  }

  const handleDeductionMenuOptionClick = async (option: string, index: number) => {
    if (option === 'Download Invoice') {
      getDocument(index.toString())
        .then((response) => {
          if (response.data?.downloadUrl) {
            const fileUrl = response.data.downloadUrl;
            // Trigger download
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = ''; // Browser will use the filename from the URL
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            setAlert((prev) => ({
              ...prev,
              message: 'No file found to download.',
              type: 'info',
            }));
          }
        })
        .catch((error: AxiosError<ErrorData>) => {
          if (error.response?.data.message) {
            setAlert((prev) => ({
              ...prev,
              message: error.response?.data.message || 'No File found to download.',
              type: 'info',
            }));
          } else {
            setAlert((prev) => ({
              ...prev,
              message: error.response?.data.message || 'File download error',
              type: 'error',
            }));
          }
        });
    }
  };

  const handleRetryExternalPayment = (netSettlementId: number | string) => {
    const parsedNetSettlementId = Number(netSettlementId);
    if (!Number.isFinite(parsedNetSettlementId) || parsedNetSettlementId <= 0) {
      setAlert((prev) => ({
        ...prev,
        message: 'Invalid net settlement id for retry action.',
        type: 'error',
      }));
      return;
    }

    setIsPaymentDetailsFetching(true);

    retryClientExternalAccountPayment(parsedNetSettlementId)
      .then((res) => {
        if (res.status === 200 || res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'The client payment retry has been initiated.',
            type: 'success',
          }));

          getDetails();
          return;
        }

        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: 'Unable to refresh payment details after retry. Please try again.',
          type: 'error',
        }));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleStopCheckTransaction = (
    netSettlementId: number | string,
    reason: string,
    additionalComments: string,
  ) => {
    const parsedNetSettlementId = Number(netSettlementId);
    if (!Number.isFinite(parsedNetSettlementId) || parsedNetSettlementId <= 0) {
      setAlert((prev) => ({
        ...prev,
        message: 'Invalid net settlement id for stop transaction action.',
        type: 'error',
      }));
      return;
    }

    setIsPaymentDetailsFetching(true);

    stopCheckTransaction({
      netSettlementId: parsedNetSettlementId,
      reason,
      additionalComments,
    })
      .then((res) => {
        if (res.status === 200 || res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'Stop check transaction has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleStopDeductionTransaction = (
    deductionId: number | string,
    reason: string,
    additionalComments: string,
  ) => {
    const parsedDeductionId = Number(deductionId);
    if (!Number.isFinite(parsedDeductionId) || parsedDeductionId <= 0) {
      setAlert((prev) => ({
        ...prev,
        message: 'Invalid deduction id for stop transaction action.',
        type: 'error',
      }));
      return;
    }

    setIsPaymentDetailsFetching(true);

    stopDeductionTransaction({
      deductionId: parsedDeductionId,
      reason,
      additionalComments,
    })
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          setAlert((prev) => ({
            ...prev,
            message: 'Deduction stop transaction has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleRetryAttyFeeVendorPayment = (attyFeeId: number) => {
    setIsPaymentDetailsFetching(true);

    retryAttyFeeVendorPayment(attyFeeId)
      .then((res) => {
        if (res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'The vendor payment retry has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  const handleRetryAttyFeeVendorPaymentWithUpdate = (
    attyFeeId: number,
    vendorPaymentAccountId: number,
    paymentMethod: string,
    vendorPaymentAccountName?: string | null,
  ) => {
    setIsPaymentDetailsFetching(true);

    // paymentMethod is only used to update the row locally; the API derives it from the account.
    retryAttyFeeVendorPaymentWithUpdate({ attyFeeId, vendorPaymentAccountId })
      .then((res) => {
        if (res.status === 200 || res.status === 202) {
          setWavesData((prev) => {
            const next: WavesDataMap = { ...prev };
            Object.keys(next).forEach((waveKey) => {
              const currentWave = next[waveKey];
              next[waveKey] = {
                ...currentWave,
                attorneysFees: currentWave.attorneysFees.map((fee) =>
                  fee.attyFeeId === attyFeeId
                    ? {
                        ...fee,
                        paymentMethod: paymentMethod as PaymentMethodStatus,
                        paymentAccountId: vendorPaymentAccountId,
                        paymentAccountName: vendorPaymentAccountName ?? null,
                      }
                    : fee,
                ),
              };
            });
            return next;
          });

          setAlert((prev) => ({
            ...prev,
            message: 'The vendor payment retry has been initiated.',
            type: 'success',
          }));

          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  /**
   * The stop call is synchronous (waits for Passport to report STOPPED), so instead of the
   * full-page loader the table keeps the dialog open with a button spinner until this resolves.
   * Resolves to true on success so the dialog knows whether to close.
   */
  const handleStopAttyFeeTransaction = (
    attyFeeId: number,
    reason: string,
    additionalComments: string,
  ): Promise<boolean> =>
    stopAttyFeeTransaction({ attyFeeId, reason, additionalComments })
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Attorney fee transaction has been stopped.',
          type: 'success',
        }));

        getDetails();
        return true;
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
        return false;
      });

  const handleResendInvite = () => {
    const customerId = settlementDetails?.customerId;
    if (!customerId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Customer id is not available for re-initiating onboarding.',
        type: 'error',
      }));
      return;
    }

    reInitiateCustomerOnboarding(customerId, settlementId ? Number(settlementId) : undefined)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message:
            'The Onboarding process has been re-initiated. Please check your email for more information.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleRetryNetsuite = () => {
    setIsPaymentDetailsFetching(true);

    retryNetsuite(Number(settlementId))
      .then((res) => {
        if (res.status === 200) {
          setAlert((prev) => ({
            ...prev,
            message: res.data?.message || `The ${vendorType} retry has been initiated.`,
            type: 'success',
          }));
          getDetails();
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsPaymentDetailsFetching(false);
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsPaymentDetailsFetching(false);
      });
  };

  return (
    <Box className={classes.pageContainer}>
      <Box className={classes.settlementDetailsContainer}>
        <Box className={classes.settlementFundingContainer}>
          {/* Onboarding Warning Banner */}
          {!!settlementDetails?.hasOnboardingPending && showUserOnboardingDetails && (
            <Box className={classes.warningMessageContainer}>
              <WarningIcon
                sx={{ color: theme.palette.warning.main, fontSize: '24px', height: 'auto' }}
              />
              <Typography className={classes.warningMessageText}>
                {settlementDetails.onboardingPendingMessage}
              </Typography>
            </Box>
          )}

          {/* {settlementDetails?.resendOnboardingInvite && (
            <Box className={classes.infoMessageContainer}>
              <Typography className={classes.infoMessageText}>
                Onboarding
              </Typography>
              <Chip
                label="Resend Invite"
                variant="outlined"
                disabled={settlementDetails.resendOnboardingInvite !== true}
                onClick={() => handleResendInvite(settlementDetails.customerId)}
                sx={{
                  height: '25px',
                  borderRadius: '12px',
                  border: '1px solid #38BDF8',
                  background: '#38BDF814',
                  width: 'fit-content',
                  '& .MuiChip-label': {
                    fontFamily: 'Inter',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#38BDF8',
                    padding: '4px 12px',
                  },
                  '&.Mui-disabled': {
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-disabled .MuiChip-label': {
                    color: 'rgba(255, 255, 255, 0.35)',
                  },
                }}
              />
            </Box>
          )} */}

          <Box className={classes.settlementSummarySection}>
            {/* Settlement Summary Heading */}
            <Box m={2}>
              <Typography
                className={clsx(
                  classes.commonText,
                  classes.fontSize20,
                  classes.fontFamilyPoppins,
                  classes.fontWeight700,
                )}
                lineHeight="120%"
                width="100%"
              >
                Settlement Summary
              </Typography>
            </Box>
            <Box className={classes.summarySectionDivider} />
            {/* Accordions below the heading, full width */}
            <Box>
              {settlementDetails?.distributions?.map((wave, idx, arr) => {
                const waveTitle = `Wave ${wave.sequenceNumber} - Settled Amount`;
                const waveStatus = wave.status;
                const statusCounts = {
                  Paid: wave.totalPaidCount || 0,
                  Failed: wave.totalFailedCount || 0,
                  NotStarted: wave.totalNotStartedCount || 0,
                  Funding: wave.totalFundingCount || 0,
                  InDistribution: wave.totalInDistributionCount || 0,
                };

                return (
                  <>
                    <Accordion key={wave.sequenceNumber} className={classes.accordionRoot}>
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{
                              color: 'white',
                              background: '#555969',
                              borderRadius: '8px',
                            }}
                          />
                        }
                        className={classes.accordionSummary}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          width="100%"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap="20px" paddingLeft="6px">
                            <Typography>{waveTitle}</Typography>
                            {waveStatus && (
                              <StatusChip
                                label={
                                  SettlementStatusMap[waveStatus as SettlementStatus]?.label ||
                                  waveStatus
                                }
                                color={
                                  SettlementStatusMap[waveStatus as SettlementStatus]?.color ||
                                  'default'
                                }
                              />
                            )}

                            <Box display="flex" alignItems="center" gap="10px">
                              {Object.entries(statusCounts).map(([status, count]) => {
                                if (count === 0) return null;

                                const statusInfo = SettlementPaymentFilterStatusMap[status];

                                return (
                                  <Tooltip
                                    key={status}
                                    arrow
                                    title={`${count} ${status}`}
                                    slotProps={{
                                      tooltip: {
                                        sx: (theme) => ({
                                          backgroundColor: theme.palette.common.white,
                                          color: theme.palette.primary.light,
                                          borderRadius: '5px',
                                          boxShadow: '3px',
                                          fontFamily: 'Inter',
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          padding: '5px',
                                          maxWidth: '140px',
                                        }),
                                      },
                                      arrow: {
                                        sx: (theme) => ({
                                          color: theme.palette.common.white,
                                          '&:before': { boxShadow: 3 },
                                        }),
                                      },
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 23,
                                        height: 23,
                                        borderRadius: '50%',
                                        background: statusInfo.color,
                                        border: '1px solid #e0e0e0',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '13px',
                                        color: '#ffffff',
                                        fontWeight: 800,
                                        textShadow: '1px 1px 4px #000000',
                                      }}
                                    >
                                      {count}
                                    </div>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        {/* 🔷 Attorneys' Fees Section   */}
                        {wave.attorneysFees?.totalAmount !== 0 && (
                          <Box className={classes.sectionBlock}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography className={classes.sectionTitle}>
                                Attorneys’ Fees
                              </Typography>
                              <Typography className={classes.amountBold}>
                                {numberToUSDCurrency(wave.attorneysFees?.totalAmount || 0)}
                              </Typography>
                            </Box>
                            <Box className={classes.distributionsSectionDivider} />
                            {/* Lead Firm */}
                            {wave.attorneysFees?.leadFirm && (
                              <Box className={classes.row}>
                                <Typography className={classes.label}>
                                  {wave.attorneysFees.leadFirm.name} - Lead Counsel
                                </Typography>
                                <Typography className={classes.amountCenter}>
                                  {numberToUSDCurrency(wave.attorneysFees.leadFirm.amount)}
                                </Typography>
                                {wave.attorneysFees.leadFirm.status && (
                                  <Box className={classes.statusBadge}>
                                    <StatusChip
                                      label={
                                        AttorneysFeeStatusMap[
                                          wave.attorneysFees.leadFirm.status as AttorneysFeeStatus
                                        ].label
                                      }
                                      color={
                                        AttorneysFeeStatusMap[
                                          wave.attorneysFees.leadFirm.status as AttorneysFeeStatus
                                        ].color
                                      }
                                    />
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Secondary Firms */}
                            {renderAttorneyFees(wave.attorneysFees, classes)}
                          </Box>
                        )}

                        {/* Deductions Section     */}

                        {wave.deductionsTotalAmount !== 0 && (
                          <Box className={classes.sectionBlock}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography className={classes.sectionTitle}>Deductions</Typography>
                              <Typography className={classes.amountBold}>
                                {numberToUSDCurrency(wave.deductionsTotalAmount || 0)}
                              </Typography>
                            </Box>
                            <Box className={classes.distributionsSectionDivider} />
                            {renderTotals(
                              {
                                totalLienPaid: wave.totalLienPaid,
                                totalLienNotStarted: wave.totalLienNotStarted,
                                totalLienFailed: wave.totalLienFailed,
                                totalLienFunding: wave.totalLienFunding,
                                totalLienInDistribution: wave.totalLienInDistribution,
                                totalServiceProviderPaid: wave.totalServiceProviderPaid,
                                totalServiceProviderNotStarted: wave.totalServiceProviderNotStarted,
                                totalServiceProviderFailed: wave.totalServiceProviderFailed,
                                totalServiceProviderFunding: wave.totalServiceProviderFunding,
                                totalServiceProviderInDistribution:
                                  wave.totalServiceProviderInDistribution,
                                totalExpensePaid: wave.totalExpensePaid,
                                totalExpenseNotStarted: wave.totalExpenseNotStarted,
                                totalExpenseFailed: wave.totalExpenseFailed,
                                totalExpenseFunding: wave.totalExpenseFunding,
                                totalExpenseInDistribution: wave.totalExpenseInDistribution,
                                lienFailedCount: wave.lienFailedCount,
                                lienPaidCount: wave.lienPaidCount,
                                lienFundingCount: wave.lienFundingCount,
                                lienNotStartedCount: wave.lienNotStartedCount,
                                lienInDistributionCount: wave.lienInDistributionCount,
                                serviceProviderFailedCount: wave.serviceProviderFailedCount,
                                serviceProviderPaidCount: wave.serviceProviderPaidCount,
                                serviceProviderFundingCount: wave.serviceProviderFundingCount,
                                serviceProviderNotStartedCount: wave.serviceProviderNotStartedCount,
                                serviceProviderInDistributionCount:
                                  wave.serviceProviderInDistributionCount,
                                expenseFailedCount: wave.expenseFailedCount,
                                expensePaidCount: wave.expensePaidCount,
                                expenseFundingCount: wave.expenseFundingCount,
                                expenseNotStartedCount: wave.expenseNotStartedCount,
                                expenseInDistributionCount: wave.expenseInDistributionCount,
                              },
                              classes,
                            )}
                          </Box>
                        )}
                        {/* ============================= */}
                        {/* 🟩 Net Settlement Section     */}
                        {/* ============================= */}
                        <Box className={classes.sectionBlock}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap="6px">
                              <Box
                                onMouseEnter={(e) => handleHoverCardOpen(e, wave)}
                                onMouseLeave={handleHoverCardClose}
                                className={classes.infoIconClickableBox}
                              >
                                <InfoOutlinedIcon className={classes.infoIcon} />
                              </Box>
                              <Typography className={classes.sectionTitle}>
                                Net Settlement to Client
                              </Typography>
                            </Box>
                            <Typography className={classes.amountBold}>
                              {numberToUSDCurrency(wave.netSettlement?.amount || 0)}
                            </Typography>
                          </Box>
                          <Box className={classes.distributionsSectionDivider} />
                          <Box className={classes.row}>
                            <Typography className={classes.label}>Net Settlement</Typography>
                            <Typography className={classes.amountCenter}>
                              {numberToUSDCurrency(wave.netSettlement?.amount || 0)}
                            </Typography>
                            {wave.netSettlement?.status && (
                              <Box className={classes.statusBadge}>
                                <StatusChip
                                  label={
                                    NetSettlementStatusMap[
                                      wave.netSettlement?.status as NetSettlementStatus
                                    ].label
                                  }
                                  color={
                                    NetSettlementStatusMap[
                                      wave.netSettlement?.status as NetSettlementStatus
                                    ].color
                                  }
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    {idx !== arr.length - 1 && <Box className={classes.summarySectionDivider} />}
                  </>
                );
              })}
            </Box>

            {/* Hover Card for Wave Info */}
            <DetailsHoverCard
              anchorEl={hoverCardAnchorEl}
              open={Boolean(hoverCardAnchorEl)}
              onClose={handleHoverCardClose}
              onMouseEnter={handleHoverCardMouseEnter}
              distribution={hoveredWave}
              total={settlementDetails?.settledAmount}
              nonNegotiationAmountToClient={hoveredWave?.nonNegotiationAmountToClient}
              netSettlementStatus={hoveredWave?.netSettlement?.status as NetSettlementStatus}
            />
          </Box>
        </Box>

        <Box className={classes.actionsContainer}>
          <Typography
            className={clsx(
              classes.commonText,
              classes.fontSize20,
              classes.fontFamilyPoppins,
              classes.fontWeight700,
            )}
            pb="12px"
          >
            Actions
          </Typography>
          <Box display="flex" flexDirection="column" gap="20px">
            {showUserOnboardingDetails && (
              <CommonButton
                disabled={!settlementDetails?.resendOnboardingInvite}
                type="skyBlue"
                buttonText="Resend Invite"
                onButtonClick={handleResendInvite}
              />
            )}
            {/* {settlementDetails.retryAvailable && (

              <CommonButton
                disabled={!settlementDetails.retryAvailable}
                type='infoDark'
                buttonText='Retry'
                maxWidth='auto'
                onButtonClick={() => handleOpenVerificationPopup(Action.Retry)}
              />

            )} */}

            <CommonButton type="infoDark" buttonText="Export" onButtonClick={exportList} />
            <CommonButton
              type="infoMain"
              buttonText="View Document"
              onButtonClick={handleViewDocument}
            />
            {settlementDetails?.isAccountingRetryAvailable && (
              <CommonButton
                type="infoDark"
                buttonText="Retry Accounting"
                onButtonClick={handleRetryNetsuite}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Settlement Payment Details Section */}
      <Box mt="40px">
        <Typography
          className={clsx(
            classes.commonText,
            classes.fontSize24,
            classes.fontWeight700,
            classes.fontFamilyPoppins,
          )}
          pb="20px"
        >
          Settlement Payment Details
        </Typography>

        <Box className={classes.tabsContainer} sx={{ mb: 3 }}>
          <Tabs value={activeWave} onChange={(e, v) => setActiveWave(v)} sx={{ mb: 2 }}>
            {Object.keys(wavesData).map((waveKey) => (
              <Tab key={waveKey} label={waveKey.replace('wave', 'Wave ')} value={waveKey} />
            ))}
          </Tabs>

          {
            <Box className={classes.paymentDetailsContent}>
              {/* Filter boxes with counts and icons */}
              <Box display="flex" gap="16px" flexWrap="wrap">
                {DetailsFilter.map((statusItem) => {
                  const status = statusItem.value;
                  const statusCounts: Record<string, number> = {
                    Funding: totalStatusCounts.Funding,
                    Paid: totalStatusCounts.Paid,
                    Failed: totalStatusCounts.Failed,
                    NotStarted: totalStatusCounts.NotStarted,
                    InDistribution: totalStatusCounts.InDistribution,
                  };

                  const getStatusIcon = (iconName: string) => {
                    switch (iconName) {
                      case 'Funding':
                        return <MonetizationOnOutlinedIcon />;
                      case 'Paid':
                        return <CheckCircleIcon />;
                      case 'Failed':
                        return <ErrorIcon />;
                      case 'InDistribution':
                        return <PendingOutlinedIcon />;
                      case 'NotStarted':
                        return <NotStartedOutlinedIcon />;
                      default:
                        return null;
                    }
                  };

                  return (
                    <Box
                      key={status}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === status
                            ? undefined
                            : (status as
                              | 'Funding'
                              | 'Failed'
                              | 'Paid'
                              | 'NotStarted'
                              | 'InDistribution'),
                        )
                      }
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        backgroundColor:
                          activeFilter === status ? 'rgba(37, 157, 168, 0.15)' : 'transparent',
                        border: `1px solid ${theme.palette.secondary.dark}`,
                        transition: 'all 0.2s ease',
                        maxWidth: '200px',
                        flex: '1 1 auto',
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography
                          className={clsx(
                            classes.commonText,
                            classes.fontSize12,
                            classes.fontFamilyPoppins,
                            classes.textGrayColor,
                          )}
                          pb="4px"
                        >
                          {status.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()}
                        </Typography>
                        <Typography
                          className={clsx(
                            classes.commonText,
                            classes.fontSize32,
                            classes.fontFamilyPoppins,
                            classes.fontWeight700,
                          )}
                        >
                          {statusCounts[status]}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: `2px solid ${SettlementPaymentFilterStatusMap[status].color}`,
                          color: SettlementPaymentFilterStatusMap[status].color,
                          fontSize: '18px',
                          fontWeight: 'bold',
                        }}
                      >
                        {getStatusIcon(SettlementPaymentFilterStatusMap[status].iconName)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Show filtered results or no results message */}
              {activeFilter &&
                (!filteredAttorneyFeesFields || filteredAttorneyFeesFields.length === 0) &&
                (!filteredDeductionsFields || filteredDeductionsFields.length === 0) &&
                !filteredNetSettlement ? (
                <Box mt="24px" textAlign="center" py={4}>
                  <Typography className={classes.textGrayColor} fontSize="16px">
                    No records found for the selected filter: {activeFilter}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box mt="24px">
                    <DetailsAttorneyTable
                      fields={filteredAttorneyFeesFields ?? []}
                      secondaryFirmsData={entityData?.secondaryFirms}
                      leadFirmData={entityData?.firm}
                      onRetryVendorPayment={handleRetryAttyFeeVendorPayment}
                      onRetryVendorPaymentWithUpdate={handleRetryAttyFeeVendorPaymentWithUpdate}
                      onStopAttyFeeTransaction={handleStopAttyFeeTransaction}
                    />
                  </Box>
                  <Box mt="24px">
                    <DetailsDeductionTable
                      fields={filteredDeductionsFields}
                      onRetryVendorPayment={handleRetryVendorPayment}
                      onRetryVendorPaymentWithUpdate={handleRetryVendorPaymentWithUpdate}
                      onStopDeductionTransaction={handleStopDeductionTransaction}
                      handleMenuOptionClick={handleDeductionMenuOptionClick}
                    />
                  </Box>
                  <Box mt="24px">
                    {/* {settlementDetails && filteredNetSettlement && ( */}
                    <DetailsNetSettlementTable
                      fields={filteredNetSettlement}
                      settlementType={entityData?.settlementType as string}
                      pipAmount={entityData?.pipAmount as number}
                      lienReimbursementAmount={entityData?.lienReimbursementAmount as number}
                      onRetryExternalPayment={handleRetryExternalPayment}
                      onStopCheckTransaction={handleStopCheckTransaction}
                    />
                    {/* )} */}
                  </Box>
                  {showApprovalFlow && (
                    <Box mt="24px">
                      <ApprovalDetails approvalDetails={filteredApprovalDetails} />
                    </Box>
                  )}
                </>
              )}
            </Box>
          }
        </Box>
      </Box>

      <VerificationPopup
        isOpen={verificationPopupState.isOpen}
        onSuccess={getActionHandler(verificationPopupState.action)}
        onCancel={handleCloseVerificationPopup}
      />
    </Box>
  );
};

export default SettlementsDetailsView;
